import React, { useState, useEffect } from 'react';
import { Users, Mail, Phone, BookOpen, Loader, AlertCircle, User, MessageCircle, ChevronDown } from 'lucide-react';
import Layout from '../../Layout';
import ParentAPI from '../../services/ParentAPI';

const LearnerTeachers = () => {
    const [children, setChildren] = useState([]);
    const [selectedChild, setSelectedChild] = useState(null);
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchChildren();
    }, []);

    useEffect(() => {
        if (selectedChild) {
            fetchChildTeachers(selectedChild.student_id || selectedChild.id);
        }
    }, [selectedChild]);

    const fetchChildren = async () => {
        try {
            const childrenData = await ParentAPI.getChildren();
            setChildren(childrenData);
            if (childrenData.length > 0) {
                setSelectedChild(childrenData[0]);
            }
        } catch (err) {
            setError('Failed to fetch children');
            console.error(err);
        }
    };

    const fetchChildTeachers = async (childId) => {
        setLoading(true);
        try {
            const teachersData = await ParentAPI.getChildTeachers(childId);
            setTeachers(teachersData);
        } catch (err) {
            setError('Failed to fetch teachers');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleChildSelect = (child) => {
        setSelectedChild(child);
    };

    const getTeacherInitials = (name) => {
        if (!name) return 'T';
        return name
            .split(' ')
            .map(word => word.charAt(0))
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    if (loading && !selectedChild) {
        return (
            <Layout pageTitle="Learner's Teachers">
                <div className="min-h-screen font-[poppins] flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                        <Loader className="h-12 w-12 animate-spin text-[#F17C45] mx-auto mb-4" />
                        <p className="text-gray-600">Loading teachers...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout pageTitle="Learner's Teachers">
                <div className="min-h-screen font-[poppins] flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Teachers</h2>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#F17C45] hover:bg-[#E16A3A]"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Learner's Teachers">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
                
                .card-parent {
                    border: 1px solid #e5e7eb;
                    background: white;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                }
                
                .card-parent:hover {
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                }
                
                .teacher-card {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    transition: box-shadow 0.2s ease;
                }
                
                .teacher-card:hover {
                    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
                }
                
                .gradient-text {
                    background: linear-gradient(135deg, #059669, #3b82f6);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
                
                .child-selector {
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    transition: all 0.2s ease;
                }
                
                .child-selector:hover {
                    background: #f3f4f6;
                }
                
                .child-selector.selected {
                    background: linear-gradient(135deg, #059669, #3b82f6);
                    color: white;
                }
            `}</style>

            <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 font-['Poppins',sans-serif]">
                {/* Header */}
                <header className="bg-gradient-to-r from-green-600 to-blue-600 shadow-2xl">
                    <div className="max-w-7xl mx-auto px-6 py-6">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="flex items-center space-x-4">
                                <div>
                                    <Users className="w-12 h-12 text-white"/>
                                </div>
                                <div>
                                    <h1 className="text-3xl font-bold text-white">
                                        {selectedChild ? `${selectedChild.name}'s Teachers` : "Learner's Teachers"}
                                    </h1>
                                    <p className="text-green-100">
                                        Connect with your child's teachers
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-4">
                                <div className="relative">
                                    <select
                                        value={selectedChild?.student_id || selectedChild?.id || ''}
                                        onChange={(e) => {
                                            const child = children.find(c => (c.student_id || c.id) === e.target.value);
                                            if (child) handleChildSelect(child);
                                        }}
                                        className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium border border-white/30 appearance-none cursor-pointer pr-10"
                                        disabled={!children.length}
                                    >
                                        {children.length === 0 ? (
                                            <option value="" className="text-gray-900">No children linked</option>
                                        ) : (
                                            children.map(child => (
                                                <option key={child.student_id || child.id} value={child.student_id || child.id} className="text-gray-900">
                                                    {child.name} - Grade {child.grade || 'N/A'}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                    <ChevronDown className="w-4 h-4 text-white absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                <div className="max-w-7xl mx-auto px-6 py-8">
                    {!selectedChild ? (
                        <div className="text-center py-12">
                            <div className="bg-white rounded-3xl shadow-xl p-12 card-parent">
                                <Users className="w-20 h-20 mx-auto mb-6 text-green-500" />
                                <h2 className="text-3xl font-bold gradient-text mb-4">Select a Learner</h2>
                                <p className="text-gray-600 text-lg mb-6">
                                    {children.length === 0 
                                        ? "No children are linked to your account. Please contact your school administrator."
                                        : "Choose a child from the dropdown above to view their teachers."
                                    }
                                </p>
                                {children.length > 0 && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
                                        {children.map((child) => (
                                            <div
                                                key={child.student_id || child.id}
                                                onClick={() => handleChildSelect(child)}
                                                className="child-selector p-6 rounded-2xl cursor-pointer"
                                            >
                                                <div className="text-center">
                                                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-blue-500 flex items-center justify-center mx-auto mb-4">
                                                        <span className="text-white font-bold text-xl">
                                                            {child.name.charAt(0).toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <h3 className="font-bold text-gray-900 text-lg">{child.name}</h3>
                                                    <p className="text-gray-600">Grade {child.grade || 'N/A'}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {/* Child Info Card */}
                            <div className="bg-white rounded-3xl shadow-xl p-6 card-parent">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-blue-500 flex items-center justify-center">
                                            <span className="text-white font-bold text-xl">
                                                {selectedChild.name.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <h2 className="text-2xl font-bold gradient-text">{selectedChild.name}</h2>
                                            <p className="text-gray-600 font-medium">
                                                Grade {selectedChild.grade || 'N/A'} • {selectedChild.school || 'School'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-600">{teachers.length}</div>
                                        <div className="text-sm text-gray-600">Teachers</div>
                                    </div>
                                </div>
                            </div>

                            {/* Teachers Content */}
                            <div className="bg-white rounded-3xl shadow-xl overflow-hidden card-parent">
                                <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-blue-50 border-b border-gray-100">
                                    <h2 className="text-xl font-bold gradient-text">Teachers</h2>
                                </div>
                                
                                <div className="p-6">
                                    {loading ? (
                                        <div className="text-center py-12">
                                            <Loader className="w-12 h-12 animate-spin text-green-500 mx-auto mb-4" />
                                            <h3 className="text-xl font-bold text-gray-800 mb-2">Loading Teachers</h3>
                                            <p className="text-gray-600">Please wait while we fetch the latest information...</p>
                                        </div>
                                    ) : teachers.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {teachers.map((teacher, index) => (
                                                <div
                                                    key={teacher.teacher_id || teacher.id || index}
                                                    className="teacher-card p-6"
                                                >
                                                    <div className="flex items-start">
                                                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-4 flex-shrink-0">
                                                            <span className="text-white font-semibold text-lg">
                                                                {getTeacherInitials(teacher.name)}
                                                            </span>
                                                        </div>
                                                        <div className="flex-1">
                                                            <h4 className="text-lg font-bold text-gray-900 mb-2">
                                                                {teacher.name || 'Teacher'}
                                                            </h4>
                                                            
                                                            {teacher.subject && (
                                                                <div className="flex items-center mb-3">
                                                                    <BookOpen className="h-4 w-4 text-gray-400 mr-2" />
                                                                    <span className="text-sm text-gray-600">
                                                                        {teacher.subject}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            
                                                            {teacher.email && (
                                                                <div className="flex items-center mb-3">
                                                                    <Mail className="h-4 w-4 text-gray-400 mr-2" />
                                                                    <a 
                                                                        href={`mailto:${teacher.email}`}
                                                                        className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                                                                    >
                                                                        {teacher.email}
                                                                    </a>
                                                                </div>
                                                            )}
                                                            
                                                            {teacher.phone && (
                                                                <div className="flex items-center mb-4">
                                                                    <Phone className="h-4 w-4 text-gray-400 mr-2" />
                                                                    <a 
                                                                        href={`tel:${teacher.phone}`}
                                                                        className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                                                                    >
                                                                        {teacher.phone}
                                                                    </a>
                                                                </div>
                                                            )}
                                                            
                                                            {teacher.department && (
                                                                <div className="mb-4">
                                                                    <span className="text-xs bg-gray-100 text-gray-800 px-3 py-1 rounded-full font-medium">
                                                                        {teacher.department}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            
                                                            <div className="flex space-x-2">
                                                                {teacher.email && (
                                                                    <button className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors">
                                                                        <Mail className="h-4 w-4 mr-2" />
                                                                        Email
                                                                    </button>
                                                                )}
                                                                <button className="flex items-center px-4 py-2 text-sm font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                                                                    <MessageCircle className="h-4 w-4 mr-2" />
                                                                    Message
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-16">
                                            <div className="mb-6">
                                                <Users className="w-20 h-20 text-gray-300 mx-auto" />
                                            </div>
                                            <h3 className="text-2xl font-bold text-gray-800 mb-4">No Teachers Found</h3>
                                            <p className="text-gray-600 text-lg">
                                                No teachers are assigned to {selectedChild?.name} yet.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default LearnerTeachers;